import { create } from 'zustand';
import { FORK_INTENT_KEY, forkIntentResumePath } from '../utils/forkIntent';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { getCurrentUser, logout as apiLogout, ApiError } from '../services';
import { readAuthHandoff, clearAuthHandoff } from '../utils/authHandoff';

const STALE_AFTER = 5 * 60 * 1000; // 5 minutes

// Concurrent fetchUser calls (React 18 StrictMode double-invokes effects)
// must share one request: the carry-over resolution inside is not idempotent
// — two racing calls would resolve the pending plan twice.
let fetchInFlight: Promise<void> | null = null;

interface UserState {
  // State
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  lastFetchedAt: number | null;

  // Actions
  fetchUser: () => Promise<void>;
  _fetchUserInner: () => Promise<void>;
  _fetchUserBody: (handoff: boolean) => Promise<void>;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
  clearError: () => void;
}

/**
 * Carry the anonymous plans-chat session (transcript + pending plan) into the
 * signed-in account `user`. Best-effort: a failed resolve keeps every key, so
 * the pending plan stays stored and a later call can retry.
 *
 * `fetchUser` runs it before exposing the session (below). Exported so the
 * save bar can retry a failed save for a signed-in user — the same function,
 * not a second save path. Its body is the block that sat inline in
 * `_fetchUserInner`, moved unchanged apart from removing the pending plan's
 * owner key (`PENDING_PLAN_OWNER_KEY` in `usePlanChat`) beside the plan, and
 * removing the plan's keys only while the stored plan is still the one this
 * call sent (see the compare-and-remove below).
 *
 * Resolves to the saved plan's id when the response carries a positive integer
 * `itinerary_id`, else `null` (a conversation-only resolve, a malformed id,
 * nothing sent, or any throw). Only `_fetchUserInner` reads it, and only on a
 * completed email auth handoff (post-signup landing, Decision 2); the save
 * bar's retry keeps awaiting it for its side effects.
 */
export async function resolvePendingAnonSession(user: Pick<User, 'id'>): Promise<number | null> {
  try {
    const anonToken = localStorage.getItem('vizday_anon_token');
    if (anonToken) {
      const { mergeAnonymousCounter } = await import('../services/plansChatApi');
      await mergeAnonymousCounter(anonToken);
    }
    const pendingPlan = localStorage.getItem('vizday_pending_plan');
    const pendingMessages = localStorage.getItem('vizday_plans_chat_messages');
    const pendingEntryId = localStorage.getItem('vizday_anon_plan_chat_entry_id');
    // Resolve whenever there's anything to carry over — a conversation
    // can hit the signup gate without a plan having been built yet.
    if (pendingPlan || pendingMessages) {
      const { resolveAnonymousSession } = await import('../services/plansChatApi');
      const messages = pendingMessages ? JSON.parse(pendingMessages) : [];
      const resolved = await resolveAnonymousSession(
        messages,
        pendingPlan ? JSON.parse(pendingPlan) : null,
        anonToken,
        pendingEntryId,
      );
      // Compare-and-remove: clear the plan this resolve carried, never a
      // newer one. Another tab can store plan B (with its owner) while this
      // request is out; removing unconditionally erased B unsaved. The
      // tracked flag and the anonymous token stay with B: the resolve
      // endpoint refuses a call without the token (400), so dropping it
      // would leave B no later resolve at all.
      if (localStorage.getItem('vizday_pending_plan') === pendingPlan) {
        localStorage.removeItem('vizday_pending_plan');
        localStorage.removeItem('vizday_pending_plan_owner');
        localStorage.removeItem('vizday_anon_plan_tracked');
        localStorage.removeItem('vizday_anon_token');
      }
      localStorage.removeItem('vizday_anon_plan_chat_entry_id');
      if (resolved?.conversation_id) {
        // Hand the resolved conversation to usePlanChat so the chat
        // continues where the anonymous session left off; keep the
        // transcript so the UI can rehydrate it.
        localStorage.setItem(
          `vizday_plans_chat_convo_v1:${user.id}`,
          resolved.conversation_id,
        );
      } else {
        localStorage.removeItem('vizday_plans_chat_messages');
      }
      const saved = resolved?.itinerary_id;
      return typeof saved === 'number' && Number.isInteger(saved) && saved > 0 ? saved : null;
    } else if (anonToken) {
      localStorage.removeItem('vizday_anon_token');
    }
    return null;
  } catch {
    // Resolution is retry-safe. Preserve the claimed envelope after a
    // timeout/transport failure; deleting it here made an uncertain
    // successful delivery impossible to replay.
    return null;
  }
}

/** Whether a fork intent is stored. A read that throws counts as present, so
 *  a storage failure suppresses the landing rather than escaping to the outer
 *  catch (which would sign the user out). */
function forkIntentPresent(): boolean {
  try {
    return localStorage.getItem(FORK_INTENT_KEY) != null;
  } catch {
    return true;
  }
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isLoading: true,
      isAuthenticated: false,
      error: null,
      lastFetchedAt: null,

      // Fetch current user from API
      // Always validates session on first call (ignores persist cache until network confirms).
      // Subsequent calls within STALE_AFTER window skip the fetch.
      fetchUser: async () => {
        if (fetchInFlight) return fetchInFlight;
        fetchInFlight = get()._fetchUserInner();
        try {
          await fetchInFlight;
        } finally {
          fetchInFlight = null;
        }
      },

      _fetchUserInner: async () => {
        // Post-signup landing (Decision 2): only the load a completed email
        // authentication marked may land. Read once, here, and consumed however
        // this fetch ends — a fresh-cache return, a throw, a signed-out answer
        // or a settled resolve of any outcome.
        const handoff = readAuthHandoff();
        try {
          await get()._fetchUserBody(handoff);
        } finally {
          clearAuthHandoff();
        }
      },

      _fetchUserBody: async (handoff: boolean) => {
        const { lastFetchedAt, user: existingUser } = get();
        const hasData = existingUser !== null;
        const isFresh = lastFetchedAt !== null && Date.now() - lastFetchedAt < STALE_AFTER;

        if (hasData && isFresh) {
          // Data is fresh from a previous network-validated fetch — trust it, clear loading
          set({ isLoading: false });
          return;
        }
        // Show spinner only if we have no cached user to display
        if (!hasData) set({ isLoading: true, error: null });

        try {
          const user = await getCurrentUser();

          // Resolve anonymous carry-over BEFORE exposing the session:
          // usePlanChat snapshots the conversation id from localStorage at
          // mount, so the app must stay in its loading state until the
          // resolved id has been written.
          let landing = await resolvePendingAnonSession(user);

          // Resume a save that hit the signup gate (spec §22.3).
          //
          // BEFORE auth becomes visible, for the same reason the chat resolve
          // above is: the app must stay in its loading state until this has
          // finished. Publishing the user first let the destination page
          // fetch and render "Save this plan" while the replay was still in
          // flight, and nothing corrected it afterwards.
          let pendingSaveForRetry: import('../services/atlasApi').PendingSave | null = null;
          // A taken destination save suppresses the landing whatever its
          // outcome (Decision 3): the guide is where Save still works.
          let pendingSaveTaken = false;
          try {
            const { takePendingSave } = await import('../services/atlasApi');
            const pendingSave = takePendingSave();
            pendingSaveForRetry = pendingSave;
            pendingSaveTaken = pendingSave != null;
            if (pendingSave) {
              const { saveDestination } = await import('../services/atlasApi');
              // Bounded: this runs BEFORE auth becomes visible, so a request
              // that never settles would hold the whole app on its loading
              // spinner forever. A save is worth a moment, not a session.
              //
              // A timeout does NOT re-queue. `Promise.race` cannot cancel the
              // request, so it is very likely still on its way — re-queuing
              // would send it again later, and could recreate a save the user
              // had removed in between. The user is signed in and the guide
              // shows its true saved state either way, so the honest failure
              // is to stop trying rather than to retry blind.
              const TIMED_OUT = Symbol('timed-out');
              const outcome = await Promise.race([
                saveDestination(pendingSave.slug, pendingSave.edition ?? undefined)
                  .then(() => 'saved' as const)
                  .catch(() => 'failed' as const),
                new Promise<typeof TIMED_OUT>((resolve) => setTimeout(
                  () => resolve(TIMED_OUT), 8000)),
              ]);
              if (outcome === TIMED_OUT) {
                pendingSaveForRetry = null;   // in flight; do not send it twice
                throw new Error('pending save timed out');
              }
              if (outcome === 'failed') throw new Error('pending save failed');
              const { trackDestinationSaved } = await import('../hooks/useAnalytics');
              trackDestinationSaved(pendingSave.slug, pendingSave.edition, 'post_signup');
              // Return to the guide, whatever route the sign-in took. Carrying
              // `returnTo` through OAuth and email verification would need
              // state plumbed through the provider round-trip and the
              // verification link; the intent already knows where it came
              // from, so it is the more reliable carrier.
              const target = pendingSave.edition
                ? `/d/${pendingSave.slug}/e/${pendingSave.edition}`
                : `/d/${pendingSave.slug}`;
              if (window.location.pathname !== target) {
                set({ user, isAuthenticated: true, isLoading: false,
                      lastFetchedAt: Date.now() });
                window.location.href = target;
                return;
              }
            }
          } catch (replayErr) {
            // takePendingSave consumed the intent, so a transient failure here
            // would lose the save silently — the one outcome someone who just
            // signed up specifically to keep something would notice.
            //
            // Re-queued with the ORIGINAL timestamp, so every retry still
            // lives inside the intent's first hour. A definite 4xx drops it
            // immediately — apiBase now attaches `.status` to every non-ok
            // response, so that check is real — but the unchanged deadline is
            // what actually bounds this, including failure modes no status
            // can classify.
            const status = (replayErr as ApiError)?.status;
            const hopeless = typeof status === 'number' && status >= 400 && status < 500;
            if (!hopeless && pendingSaveForRetry) {
              try {
                const { queuePendingSave } = await import('../services/atlasApi');
                queuePendingSave(
                  pendingSaveForRetry.slug,
                  pendingSaveForRetry.edition,
                  pendingSaveForRetry.ts,
                );
              } catch {
                // Nothing more to do — the guide is still there, and Save
                // still works.
              }
            }
          }

          // Post-signup landing (Decision 2, 3, 8). Precedence: a taken
          // destination save, then a fork intent, then the plan. The account
          // is re-read last, bounded as the save replay above is, so a logout
          // or a sign-in as someone else in another tab during the resolve
          // yields no landing; the server's owner check on /itinerary/<id> is
          // the backstop for the in-flight residual.
          if (!handoff || pendingSaveTaken) landing = null;
          if (landing != null && !forkIntentPresent()) {
            try {
              const REREAD_TIMED_OUT = Symbol('reread-timed-out');
              const again = await Promise.race([
                getCurrentUser(),
                new Promise<typeof REREAD_TIMED_OUT>((resolve) => setTimeout(
                  () => resolve(REREAD_TIMED_OUT), 8000)),
              ]);
              if (again === REREAD_TIMED_OUT || again?.id !== user.id) landing = null;
            } catch {
              landing = null;
            }
            // A fork another tab queued during the re-read keeps its order:
            // the fork block below consumes it and navigates.
            if (forkIntentPresent()) landing = null;
          } else {
            landing = null;
          }

          set({ user, isAuthenticated: true, isLoading: false, lastFetchedAt: Date.now() });

          if (landing != null && window.location.pathname !== `/itinerary/${landing}`) {
            window.location.href = `/itinerary/${landing}`;
            return;
          }

          // Resolve a queued fork intent — send the new account back to the
          // page it was forking from (public page or share page) to finish.
          try {
            const forkRaw = localStorage.getItem(FORK_INTENT_KEY);
            if (forkRaw) {
              // Consumed on read: a stale or malformed intent must not be
              // retried on every load for the life of the profile.
              localStorage.removeItem(FORK_INTENT_KEY);
              const resume = forkIntentResumePath(JSON.parse(forkRaw));
              if (resume) {
                // Navigate via window.location so the page mounts with auth.
                // fork=1 tells it to reopen the fork modal so the user doesn't
                // have to find and click "Make it mine" a second time.
                window.location.href = resume;
                return;
              }
            }
          } catch {
            localStorage.removeItem(FORK_INTENT_KEY);
          }
        } catch (err) {
          const isAuthError =
            (err instanceof Error && err.message === 'Unauthorized') ||
            (err instanceof ApiError && err.status === 403);
          if (isAuthError || !hasData) {
            // Auth failure (401/403) or first load: clear session
            set({ user: null, isAuthenticated: false, isLoading: false, lastFetchedAt: null });
          }
          // Transient error during background refresh: preserve existing session
        }
      },

      // Set user directly (for after login)
      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        });
      },

      // Logout
      logout: async () => {
        try {
          await apiLogout();
        } catch {
          // Ignore logout errors
        }
        set({
          user: null,
          isAuthenticated: false,
          lastFetchedAt: null,
        });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'vizday-user',
      // Only persist user data, not loading states
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Selector hooks for common use cases
export const useUser = () => useUserStore((state) => state.user);
export const useIsAuthenticated = () => useUserStore((state) => state.isAuthenticated);
export const useIsLoading = () => useUserStore((state) => state.isLoading);

export const isUserStale = () => {
  const { lastFetchedAt } = useUserStore.getState();
  return lastFetchedAt === null || Date.now() - lastFetchedAt > STALE_AFTER;
};

export default useUserStore;
